import {
  Box,
  Text,
  VStack,
  Button,
  Progress,
  Image,
  BoxProps,
  chakra,
  HStack,
} from "@chakra-ui/react";
import ReactS3Uploader, { S3Response } from "react-s3-uploader";

import { useRef, useState } from "react";
import { useLoading } from "src/Manager/hooks/form";
import KYDLoading from "src/ui/KYDLoading";
import { AddIcon } from "@chakra-ui/icons";

const Video = chakra("video");
interface MediaUploadAreaProps extends Omit<BoxProps, "onChange"> {
  name?: string;
  variant?: "default" | "modern";
  resolution?: string;
  ratio: string;
  url?: string;
  onClick?: () => void;
  createUploadUrl: ({
    file,
    callback,
  }: {
    file: File;
    callback: ({ signedUrl }) => void | Promise<void | string>;
  }) => Promise<{ signedUrl: string; fileKey: string }>;
  onChange: (result: {
    cdn_url: string;
    file_key: string;
    temp_url: string;
    mime_type: string;
    file: File;
  }) => void;
  showDelete?: boolean;
  noPadding?: boolean;
  uploadButton?: boolean;
  mediaType?: "photo" | "video";
}

const MediaUploadArea = ({
  name,
  variant = "default",
  mediaType = "photo",
  resolution,
  ratio,
  url,
  onClick,
  onChange,
  createUploadUrl,
  noPadding = false,
  showDelete = true,
  uploadButton = true,
  ...props
}: MediaUploadAreaProps) => {
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useLoading({
    upload: false,
  });
  const uploadRef = useRef<HTMLInputElement>(null);

  console.log({
    props,
    url,
  });

  const renderNameAndRatio = () => {
    return (
      <HStack textAlign="center" justifyContent="center">
        <Text fontSize="sm" fontWeight={"bold"} textAlign={"center"}>
          {name}
        </Text>
        <Text fontSize="xs" color="gray.500" fontWeight={"medium"}>
          {variant === "modern" ? ratio.replace("/", ":") : ""}
        </Text>
      </HStack>
    );
  };

  return (
    <Box
      rounded={"xl"}
      bg={variant === "modern" ? "transparent" : "gray.200"}
      aspectRatio={ratio}
      p={url ? props.p || 0 : 2}
      border={variant === "modern" ? "none" : "1px solid"}
      borderColor={variant === "modern" ? "transparent" : "gray.300"}
      width="100%"
      height={variant === "modern" ? "100%" : "auto"}
      {...props}
    >
      <ReactS3Uploader
        style={{ display: "none" }}
        //@ts-ignore
        inputRef={uploadRef}
        getSignedUrl={(file, callback) => createUploadUrl({ file, callback })}
        accept="image/*,video/*"
        onProgress={(p) => {
          setProgress(p);
        }}
        preprocess={(f, next) => {
          setLoading("upload", true);
          next(f);
        }}
        onError={(e) => {
          console.log("onError", e);
          console.error(e);
        }}
        onFinish={(result: S3Response & { cdn_url: string }, file) => {
          setLoading("upload", false);
          onChange({
            cdn_url: result.cdn_url,
            file_key: result.fileKey,
            temp_url: URL.createObjectURL(file),
            mime_type: file.type,
            file,
          });

          //@ts-ignore
          if (uploadRef.current) {
            uploadRef.current.value = "";
          }
        }}
        uploadRequestHeaders={(file) => ({
          "Content-Type": file.type,
          "x-amz-acl": "private",
        })}
        contentDisposition="auto"
      />

      <VStack h="100%" w="100%">
        {!url ? (
          <VStack
            alignItems={"center"}
            justifyContent={"center"}
            spacing={8}
            rounded={"xl"}
            overflow={"hidden"}
            bg={variant === "modern" ? "transparent" : "gray.200"}
            p={noPadding ? 0 : 2}
            border={noPadding ? "none" : "1px dashed"}
            borderColor="gray.300"
            cursor="pointer"
            onClick={() => (loading.upload ? null : uploadRef.current?.click())}
            opacity={loading.upload ? 0.5 : 1}
            h="100%"
            w="full"
          >
            <VStack textAlign={"center"}>
              {name && renderNameAndRatio()}
              {resolution && (
                <Text fontSize={"xs"} color="gray.500">
                  {resolution}
                </Text>
              )}
              {loading.upload ? (
                <VStack>
                  <KYDLoading strokeColor="#000000" />
                  <Progress colorScheme="blue" value={progress} />
                </VStack>
              ) : (
                <VStack alignItems={"center"} justifyContent={"center"}>
                  <Text fontSize={"xs"} color="gray.500" flex={1} flexGrow={1}>
                    Drag & drop file here
                  </Text>
                </VStack>
              )}
            </VStack>

            {uploadButton && (
              <Button
                variant={"outline"}
                border="1px solid #E2E2E2"
                fontWeight="400"
                h="40px"
                colorScheme="gray"
                fontSize="13px"
                gap={2}
                borderRadius="lg"
                bg="white"
                dropShadow="0px 2px 6px rgba(0,0,0,0.05)"
                size={"sm"}
                disabled={loading.upload}
                rightIcon={<AddIcon color="#5D5D5D" />}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  uploadRef.current?.click();
                }}
              >
                {variant === "modern" ? "Add Media" : "Upload Media"}
                {loading.upload && (
                  <Progress colorScheme="blue" value={progress} />
                )}
              </Button>
            )}
          </VStack>
        ) : (
          <VStack
            justifyContent={"space-evenly"}
            border={noPadding ? "none" : "1px dashed"}
            gap={noPadding ? 0 : 2}
            borderColor="gray.300"
            h="100%"
            w="100%"
          >
            {name && renderNameAndRatio()}
            <Box
              alignItems={"center"}
              justifyContent={"center"}
              rounded={variant === "modern" ? "none" : "xl"}
              overflow="hidden"
              aspectRatio={ratio}
              maxH="80%"
              w="auto"
              minW={0}
              bg={variant === "modern" ? "transparent" : "gray.200"}
              objectFit={variant === "modern" ? "cover" : "contain"}
              mx="auto"
              p={noPadding ? 0 : 2}
              onClick={() => uploadRef.current?.click()}
              cursor="pointer"
              as={mediaType === "video" ? Video : Image}
              src={url}
              {...(mediaType === "video" && {
                controls: true,
                autoPlay: true,
                muted: true,
                loop: true,
                style: {
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                },
              })}
            />
            {showDelete && (
              <HStack alignItems="center" justifyContent="center">
                <Text
                  as="button"
                  color="blue.500"
                  mr={4}
                  fontSize="sm"
                  fontWeight="semibold"
                  textDecoration="underline"
                  background="none"
                  border="none"
                  cursor="pointer"
                  _hover={{ color: "blue.700" }}
                  onClick={() => uploadRef.current?.click()}
                >
                  Replace
                </Text>
                <Text
                  as="button"
                  color="blue.500"
                  fontSize="sm"
                  fontWeight="semibold"
                  textDecoration="underline"
                  background="none"
                  border="none"
                  cursor="pointer"
                  _hover={{ color: "blue.700" }}
                  onClick={() =>
                    onChange({
                      cdn_url: "",
                      file_key: "",
                      temp_url: "",
                      mime_type: "",
                      file: null,
                    })
                  }
                >
                  Remove
                </Text>
              </HStack>
            )}
          </VStack>
        )}
      </VStack>
    </Box>
  );
};

export default MediaUploadArea;
